/*
  <drag-slider> — a drop-in replacement for Dawn's <slider-component>.

  Same markup interface as slider-component, so swapping the tag upgrades any
  existing slider with: mouse drag-to-scroll, a low directional flick threshold
  (a small drag advances one slide), and smooth animated navigation. The native
  slider hard-jumps (scrollTo without smoothing) and has no drag — this fixes both.

  Markup interface (identical to slider-component):
    <drag-slider data-flick="0.1">
      <ul id="Slider-…">                      ← the scrollable element
        <li id="Slide-…">…</li> …             ← each item
      </ul>
      <button name="previous">…</button>
      <button name="next">…</button>
      <span class="slider-counter--current"></span>  (optional)
      <span class="slider-counter--total"></span>     (optional)
    </drag-slider>

  The scrollable element gets an `is-dragging` class while a mouse drag is active
  (style cursor / user-select there if desired). Dispatches `slideChanged`.
*/
if (!document.getElementById('drag-slider-styles')) {
  const dragSliderStyle = document.createElement('style');
  dragSliderStyle.id = 'drag-slider-styles';
  dragSliderStyle.textContent =
    'drag-slider .is-draggable{cursor:grab}' +
    'drag-slider .is-dragging{cursor:grabbing;user-select:none}';
  document.head.appendChild(dragSliderStyle);
}

if (!customElements.get('drag-slider')) {
  customElements.define(
    'drag-slider',
    class extends HTMLElement {
      connectedCallback() {
        this.slider = this.querySelector('[id^="Slider-"]');
        if (!this.slider) return;
        this.sliderItems = Array.from(this.querySelectorAll('[id^="Slide-"]'));
        this.prevButton = this.querySelector('button[name="previous"]');
        this.nextButton = this.querySelector('button[name="next"]');
        this.currentPageElement = this.querySelector('.slider-counter--current');
        this.pageTotalElement = this.querySelector('.slider-counter--total');
        this.flickRatio = parseFloat(this.dataset.flick) || 0.1;

        this.dragging = false;
        this.programmatic = false;
        this.suppressClick = false;

        this.slider.addEventListener('scroll', () => this.onScroll(), { passive: true });
        if (this.prevButton) this.prevButton.addEventListener('click', (event) => this.onButtonClick(event));
        if (this.nextButton) this.nextButton.addEventListener('click', (event) => this.onButtonClick(event));

        this.initDrag();
        this.update();

        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(this.slider);
      }

      disconnectedCallback() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
        clearTimeout(this.snapTimer);
      }

      positions() {
        if (!this.sliderItems.length) return [0];
        const base = this.sliderItems[0].offsetLeft;
        const max = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
        return this.sliderItems.map((item) => Math.min(Math.max(Math.round(item.offsetLeft - base), 0), max));
      }

      indexNear(position) {
        const positions = this.positions();
        let index = 0;
        let smallest = Infinity;
        positions.forEach((pos, i) => {
          const distance = Math.abs(pos - position);
          if (distance < smallest) {
            smallest = distance;
            index = i;
          }
        });
        return index;
      }

      currentIndex() {
        return this.indexNear(this.slider.scrollLeft);
      }

      scrollToIndex(index) {
        const positions = this.positions();
        const target = positions[Math.min(Math.max(index, 0), positions.length - 1)];
        if (Math.abs(target - this.slider.scrollLeft) < 1) return;
        this.programmatic = true;
        clearTimeout(this.programmaticTimer);
        this.programmaticTimer = setTimeout(() => {
          this.programmatic = false;
        }, 450);
        this.slider.scrollTo({ left: target, behavior: 'smooth' });
      }

      onButtonClick(event) {
        event.preventDefault();
        const step = parseInt(event.currentTarget.dataset.step, 10) || 1;
        const direction = event.currentTarget.name === 'next' ? step : -step;
        this.scrollToIndex(this.currentIndex() + direction);
      }

      onScroll() {
        this.update();
        if (this.dragging || this.programmatic) return;
        clearTimeout(this.snapTimer);
        this.snapTimer = setTimeout(() => this.scrollToIndex(this.currentIndex()), 110);
      }

      update() {
        const max = this.slider.scrollWidth - this.slider.clientWidth;
        const x = this.slider.scrollLeft;
        const current = this.currentIndex() + 1;

        this.slider.classList.toggle('is-draggable', max > 1);

        if (this.currentPageElement && this.pageTotalElement) {
          this.currentPageElement.textContent = current;
          this.pageTotalElement.textContent = this.sliderItems.length;
        }
        if (this.prevButton) this.prevButton.toggleAttribute('disabled', x <= 1);
        if (this.nextButton) this.nextButton.toggleAttribute('disabled', x >= max - 1);

        if (current !== this.currentPage) {
          this.currentPage = current;
          this.dispatchEvent(
            new CustomEvent('slideChanged', {
              detail: { currentPage: current, currentElement: this.sliderItems[current - 1] },
            })
          );
        }
      }

      settleDrag() {
        const positions = this.positions();
        const slideWidth = positions.length > 1 ? positions[1] - positions[0] : this.slider.clientWidth;
        const startIndex = this.indexNear(this.dragStartLeft);
        const nearest = this.currentIndex();
        const delta = this.slider.scrollLeft - this.dragStartLeft;
        let target = nearest;
        if (nearest === startIndex && Math.abs(delta) > slideWidth * this.flickRatio) {
          target = startIndex + (delta > 0 ? 1 : -1);
        }
        this.scrollToIndex(target);
      }

      initDrag() {
        const el = this.slider;
        const threshold = 5;
        let pending = false;
        let startX = 0;
        let startLeft = 0;
        let pointerId = null;

        el.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0) return;
          if (el.scrollWidth - el.clientWidth <= 1) return;
          pending = true;
          this.dragging = false;
          this.suppressClick = false;
          startX = event.clientX;
          startLeft = el.scrollLeft;
          pointerId = event.pointerId;
        });

        el.addEventListener('pointermove', (event) => {
          if (!pending) return;
          const dx = event.clientX - startX;
          if (!this.dragging) {
            if (Math.abs(dx) < threshold) return;
            this.dragging = true;
            this.dragStartLeft = startLeft;
            el.classList.add('is-dragging');
            try {
              el.setPointerCapture(pointerId);
            } catch (error) {}
          }
          el.scrollLeft = startLeft - dx;
        });

        const end = () => {
          if (!pending) return;
          pending = false;
          if (!this.dragging) return;
          this.dragging = false;
          this.suppressClick = true;
          el.classList.remove('is-dragging');
          try {
            el.releasePointerCapture(pointerId);
          } catch (error) {}
          this.settleDrag();
        };

        el.addEventListener('pointerup', end);
        el.addEventListener('pointercancel', end);

        el.addEventListener(
          'click',
          (event) => {
            if (this.suppressClick) {
              event.preventDefault();
              event.stopPropagation();
              this.suppressClick = false;
            }
          },
          true
        );

        el.addEventListener('dragstart', (event) => event.preventDefault());
      }
    }
  );
}
